import { IBrand, ICarImageElement } from 'data/schema';
import * as React from 'react';
import { IMicro2AppFeatureFlags } from './index';
import { convertImagesToCompareFormat } from 'state/session/compare';

// Lazy load Micro2DApp component for code splitting
const Micro2DApp = React.lazy(() => import('./index').then((mod) => ({ default: mod.default })));

export type CompareProps = {
  car1: ICarImageElement[];
  car2: ICarImageElement[];
  userType?: 'dealer' | 'customer';
  brand?: IBrand;
  theme?: string;
  template?: string;
  features?: IMicro2AppFeatureFlags;
  customerAllowance?: boolean;
};

/**
 * Loading fallback component for Micro2DApp
 */
const Micro2DAppFallback: React.FC = () => (
  <div
    className="flex size-full items-center justify-center rounded bg-gray-900"
    role="status"
    aria-label="Loading 2D comparison"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="size-12 animate-spin rounded-full border-b-2 border-white" />
      <p className="text-sm text-white">Loading comparison...</p>
    </div>
  </div>
);

/**
 * Compare component - displays two cars side-by-side in compare mode.
 * Uses the `car1` and `car2` props with ICarImageElement[] structure.
 * Automatically converts to ICompareCarImageElement format and filters for valid comparison images.
 * Lazy loads Micro2DApp component for improved code splitting and performance.
 */
const Compare: React.FC<CompareProps> = ({
  car1,
  car2,
  userType,
  brand,
  theme,
  template,
  features,
  customerAllowance,
}) => {
  const convertedCar1 = React.useMemo(() => convertImagesToCompareFormat(car1), [car1]);
  const convertedCar2 = React.useMemo(() => convertImagesToCompareFormat(car2), [car2]);

  return (
    <React.Suspense fallback={<Micro2DAppFallback />}>
      <Micro2DApp
        mode="compare"
        car1={convertedCar1}
        car2={convertedCar2}
        userType={userType}
        brand={brand}
        theme={theme}
        template={template}
        features={features}
        customerAllowance={customerAllowance}
      />
    </React.Suspense>
  );
};

export default Compare;
